import {ApiError} from "./client";
import {ERROR_CODES} from "../generated/openapi-contract";
import type {ErrorCode} from "../generated/openapi-contract";

const knownCodes: ReadonlySet<string> = new Set<string>(ERROR_CODES);

export function isErrorCode(value: string): value is ErrorCode {
    return knownCodes.has(value);
}

/**
 * Код отказа из тела ответа (`code`, перечень — ErrorCode в OpenAPI).
 * null — ответ без кода: не JSON, старый отказ или код, которого клиент не знает.
 * Экраны различают отказы только по коду: текст `error` — для людей и может
 * меняться.
 */
export function apiErrorCode(err: unknown): ErrorCode | null {
    if (!(err instanceof ApiError) || !err.body) return null;
    try {
        const code: unknown = (JSON.parse(err.body) as {code?: unknown} | null)?.code;
        return typeof code === "string" && isErrorCode(code) ? code : null;
    } catch {
        return null;
    }
}

/** Текст экрана для кода отказа; undefined — у экрана нет своих слов для него. */
export function messageForCode(err: unknown, messages: Partial<Record<ErrorCode, string>>): string | undefined {
    const code = apiErrorCode(err);
    return code === null ? undefined : messages[code];
}

/** Русский текст из поля `error` ответа; undefined — поля нет или текст не русский. */
function serverText(err: unknown): string | undefined {
    if (!(err instanceof ApiError) || !err.body) return undefined;
    try {
        const text: unknown = (JSON.parse(err.body) as {error?: unknown} | null)?.error;
        return typeof text === "string" && /[А-Яа-яЁё]/.test(text) ? text : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Как messageForCode, но для кодов, чей текст несёт числа (диапазон, минимум,
 * число переводов): сервер подставляет в него суммы, и человеку нужны именно
 * они. Когда русского текста в ответе нет, говорит статичный текст экрана.
 * Код по-прежнему решает, о чём отказ; без своего слова у экрана — undefined.
 */
export function messageForCodeWithDetails(err: unknown, messages: Partial<Record<ErrorCode, string>>): string | undefined {
    const fallback = messageForCode(err, messages);
    return fallback === undefined ? undefined : serverText(err) ?? fallback;
}

/** Отказы формы мерчанта по условиям входящей заявки: диапазон, переводы, минимум. */
export const portalInTermsMessages: Partial<Record<ErrorCode, string>> = {
    portal_in_range_invalid: "Диапазон задан неверно: нижняя граница должна быть больше нуля и меньше верхней.",
    portal_in_transfer_count_invalid: "Количество переводов — от 1 до 20.",
    portal_in_min_transfer_invalid: "Минимальная сумма одного перевода нужна только при двух и более переводах и должна быть больше нуля.",
    portal_in_transfers_exceed_amount: "Минимальные суммы переводов вместе больше суммы заявки. Уменьшите минимум или количество переводов.",
    portal_in_terms_not_allowed: "Диапазон суммы и количество переводов доступны только для входящих заявок.",
};

/** Отказы выдачи реквизитов трейдером по условиям заявки. */
export const payinTermsMessages: Partial<Record<ErrorCode, string>> = {
    payin_amount_required: "Выберите сумму заявки из диапазона, который просит мерчант.",
    payin_amount_out_of_range: "Сумма вне диапазона заявки. Выберите сумму из подсказки.",
    payin_amount_already_chosen: "Сумма заявки уже выбрана, изменить её нельзя. Обновите карточку.",
    payin_transfer_count_mismatch: "Реквизитов больше, чем переводов может сделать мерчант: уберите лишние.",
    payin_transfer_below_minimum: "Один из переводов меньше минимума, который просит мерчант.",
};
